import { type AnnualCashFlow } from '@fruitful/financial-calculators';
import { formatDollars } from '@fruitful/fruitils';
import { useMemo } from 'react';
import { Chart } from 'react-google-charts';
import { BodyMediumRegular, tokens, View, YStack } from 'smoothie';

import { useAnnualCashFlowTotals } from '../hooks/useAnnualCashFlowTotals';
import {
    getYearIndex,
    RETIREMENT_CALCULATOR_COLORS,
} from '../utils/retirementCalculatorUtils';

interface AnnualCashFlowSankeyProps {
    annualCashFlows: AnnualCashFlow[];
    selectedYear: number;
}

export const AnnualCashFlowSankey = ({
    annualCashFlows,
    selectedYear,
}: AnnualCashFlowSankeyProps) => {
    if (annualCashFlows.length === 0) {
        return null;
    }

    // Use shared hook for totals calculation
    const totals = useAnnualCashFlowTotals(annualCashFlows, selectedYear);

    // Transform AnnualCashFlow into Google Charts Sankey data and generate chart options
    const chartDataResult = useMemo(() => {
        const selectedYearIndex = getYearIndex(selectedYear);
        const cashFlow = annualCashFlows[selectedYearIndex];

        const emptyData = {
            data: [['From', 'To', 'Weight']],
            hasData: false,
            options: {},
        };

        if (!totals || !cashFlow) {
            return emptyData;
        }

        const createNodeName = (label: string, amount?: number) => {
            if (amount !== undefined) {
                return `${label} - ${formatDollars(amount)}`;
            }
            return label;
        };

        // Map data into Sankey chart "rows" that link the nodes together.
        const rows: Array<[string, string, number]> = [];

        // The Sankey chart iterates over the specified set of colors for each node.
        // To control colors for each node, explicitly define exactly one color for each node.
        const nodeColorMap = new Map<string, string>();

        const NODE_TYPES = {
            CONTRIBUTIONS: {
                name: 'Retirement Contributions',
                color: RETIREMENT_CALCULATOR_COLORS.CONTRIBUTIONS,
            },
            GROWTH: {
                name: 'Investment Growth',
                color: RETIREMENT_CALCULATOR_COLORS.GROWTH,
            },
            DISTRIBUTIONS: {
                name: 'Withdrawals',
                color: RETIREMENT_CALCULATOR_COLORS.DISTRIBUTIONS,
            },
            SOCIAL_SECURITY: {
                name: 'Social Security Benefits',
                color: RETIREMENT_CALCULATOR_COLORS.SOCIAL_SECURITY,
            },
            INFLOWS: {
                name: 'Inflows',
                color: RETIREMENT_CALCULATOR_COLORS.INFLOWS,
            },
            EXPENSES: {
                name: 'Retirement Expenses',
                color: RETIREMENT_CALCULATOR_COLORS.EXPENSES,
            },
            HEALTHCARE_EXPENSES: {
                name: 'Healthcare',
                color: RETIREMENT_CALCULATOR_COLORS.HEALTHCARE,
            },
            LONG_TERM_CARE_EXPENSES: {
                name: 'Long Term Care',
                color: RETIREMENT_CALCULATOR_COLORS.LONG_TERM_CARE,
            },
            TAXES: {
                name: 'Taxes',
                color: RETIREMENT_CALCULATOR_COLORS.TAXES,
            },
        };

        // Individual retirement contributions → Retirement Contributions
        cashFlow.contributions.forEach((contributionItem) => {
            // Employee contributions
            if (contributionItem.employeeContribution > 0) {
                const fromNode = createNodeName(
                    `${contributionItem.account.id} (Employee)`,
                    contributionItem.employeeContribution,
                );
                const toNode = createNodeName(
                    NODE_TYPES.CONTRIBUTIONS.name,
                    totals.totalContributions,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.CONTRIBUTIONS.color);
                nodeColorMap.set(toNode, NODE_TYPES.CONTRIBUTIONS.color);

                rows.push([
                    fromNode,
                    toNode,
                    contributionItem.employeeContribution,
                ]);
            }

            // Employer contributions
            if (
                contributionItem.employerContribution != null &&
                contributionItem.employerContribution > 0
            ) {
                const fromNode = createNodeName(
                    `${contributionItem.account.id} (Employer)`,
                    contributionItem.employerContribution,
                );
                const toNode = createNodeName(
                    NODE_TYPES.CONTRIBUTIONS.name,
                    totals.totalContributions,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.CONTRIBUTIONS.color);
                nodeColorMap.set(toNode, NODE_TYPES.CONTRIBUTIONS.color);

                rows.push([
                    fromNode,
                    toNode,
                    contributionItem.employerContribution,
                ]);
            }
        });

        // Individual account growth → Investment Growth
        cashFlow.growth.forEach((growthItem) => {
            if (growthItem.growth > 0) {
                const assetName = growthItem.account.id || 'Unknown Asset';
                const fromNode = createNodeName(assetName, growthItem.growth);
                const toNode = createNodeName(
                    NODE_TYPES.GROWTH.name,
                    totals.totalGrowth,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.GROWTH.color);
                nodeColorMap.set(toNode, NODE_TYPES.GROWTH.color);

                rows.push([fromNode, toNode, growthItem.growth]);
            }
        });

        // Individual Social Security items → Social Security Benefits
        if (totals.totalSocialSecurity > 0) {
            if (cashFlow.primaryMemberSocialSecurityBenefit > 0) {
                const fromNode = createNodeName(
                    'Primary Member Social Security',
                    cashFlow.primaryMemberSocialSecurityBenefit,
                );
                const toNode = createNodeName(
                    NODE_TYPES.SOCIAL_SECURITY.name,
                    totals.totalSocialSecurity,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.SOCIAL_SECURITY.color);
                nodeColorMap.set(toNode, NODE_TYPES.SOCIAL_SECURITY.color);

                rows.push([
                    fromNode,
                    toNode,
                    cashFlow.primaryMemberSocialSecurityBenefit,
                ]);
            }
            if (
                cashFlow.partnerMemberSocialSecurityBenefit != null &&
                cashFlow.partnerMemberSocialSecurityBenefit > 0
            ) {
                const fromNode = createNodeName(
                    'Partner Member Social Security',
                    cashFlow.partnerMemberSocialSecurityBenefit,
                );
                const toNode = createNodeName(
                    NODE_TYPES.SOCIAL_SECURITY.name,
                    totals.totalSocialSecurity,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.SOCIAL_SECURITY.color);
                nodeColorMap.set(toNode, NODE_TYPES.SOCIAL_SECURITY.color);

                rows.push([
                    fromNode,
                    toNode,
                    cashFlow.partnerMemberSocialSecurityBenefit,
                ]);
            }
        }

        // Individual distributions → Withdrawals
        cashFlow.distributions.forEach((distributionItem) => {
            if (distributionItem.grossDistributionAmount > 0) {
                const fromNode = createNodeName(
                    `${distributionItem.account.id} (${distributionItem.type})`,
                    distributionItem.grossDistributionAmount,
                );
                const toNode = createNodeName(
                    NODE_TYPES.DISTRIBUTIONS.name,
                    totals.totalDistributions,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.DISTRIBUTIONS.color);
                nodeColorMap.set(toNode, NODE_TYPES.DISTRIBUTIONS.color);

                rows.push([
                    fromNode,
                    toNode,
                    distributionItem.grossDistributionAmount,
                ]);
            }
        });

        // Retirement Contributions → Inflows
        if (totals.totalContributions > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.CONTRIBUTIONS.name,
                totals.totalContributions,
            );
            const toNode = createNodeName(
                NODE_TYPES.INFLOWS.name,
                totals.totalInflow,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.CONTRIBUTIONS.color);
            nodeColorMap.set(toNode, NODE_TYPES.INFLOWS.color);

            rows.push([fromNode, toNode, totals.totalContributions]);
        }

        // Investment Growth → Inflows
        if (totals.totalGrowth > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.GROWTH.name,
                totals.totalGrowth,
            );
            const toNode = createNodeName(
                NODE_TYPES.INFLOWS.name,
                totals.totalInflow,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.GROWTH.color);
            nodeColorMap.set(toNode, NODE_TYPES.INFLOWS.color);

            rows.push([fromNode, toNode, totals.totalGrowth]);
        }

        // Withdrawals (net after taxes) → Inflows
        if (totals.totalDistributions > 0) {
            const netDistributions =
                totals.totalDistributions - totals.totalDistributionTaxes;
            if (netDistributions > 0) {
                const fromNode = createNodeName(
                    NODE_TYPES.DISTRIBUTIONS.name,
                    totals.totalDistributions,
                );
                const toNode = createNodeName(
                    NODE_TYPES.INFLOWS.name,
                    totals.totalInflow,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.DISTRIBUTIONS.color);
                nodeColorMap.set(toNode, NODE_TYPES.INFLOWS.color);

                rows.push([fromNode, toNode, netDistributions]);
            }
        }

        // Social Security Benefits (net after taxes) → Inflows
        if (totals.totalSocialSecurity > 0) {
            const netSocialSecurity =
                totals.totalSocialSecurity - totals.totalSocialSecurityTaxes;
            if (netSocialSecurity > 0) {
                const fromNode = createNodeName(
                    NODE_TYPES.SOCIAL_SECURITY.name,
                    totals.totalSocialSecurity,
                );
                const toNode = createNodeName(
                    NODE_TYPES.INFLOWS.name,
                    totals.totalInflow,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.SOCIAL_SECURITY.color);
                nodeColorMap.set(toNode, NODE_TYPES.INFLOWS.color);

                rows.push([fromNode, toNode, netSocialSecurity]);
            }
        }

        // Inflows → Retirement Expenses
        if (totals.totalInflow > 0 && cashFlow.retirementExpenses > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.INFLOWS.name,
                totals.totalInflow,
            );
            const toNode = createNodeName(
                NODE_TYPES.EXPENSES.name,
                cashFlow.retirementExpenses,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.INFLOWS.color);
            nodeColorMap.set(toNode, NODE_TYPES.EXPENSES.color);

            rows.push([fromNode, toNode, cashFlow.retirementExpenses]);
        }

        // Inflows → Retirement Healthcare Costs
        if (totals.totalInflow > 0 && cashFlow.retirementHealthcareCosts > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.INFLOWS.name,
                totals.totalInflow,
            );
            const toNode = createNodeName(
                NODE_TYPES.HEALTHCARE_EXPENSES.name,
                cashFlow.retirementHealthcareCosts,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.INFLOWS.color);
            nodeColorMap.set(toNode, NODE_TYPES.HEALTHCARE_EXPENSES.color);

            rows.push([fromNode, toNode, cashFlow.retirementHealthcareCosts]);
        }

        // Inflows → Long Term Care Costs
        if (totals.totalInflow > 0 && cashFlow.longTermCareCosts > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.INFLOWS.name,
                totals.totalInflow,
            );
            const toNode = createNodeName(
                NODE_TYPES.LONG_TERM_CARE_EXPENSES.name,
                cashFlow.longTermCareCosts,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.INFLOWS.color);
            nodeColorMap.set(toNode, NODE_TYPES.LONG_TERM_CARE_EXPENSES.color);

            rows.push([fromNode, toNode, cashFlow.longTermCareCosts]);
        }

        // Withdrawals → Taxes
        if (
            totals.totalDistributions > 0 &&
            totals.totalDistributionTaxes > 0
        ) {
            const fromNode = createNodeName(
                NODE_TYPES.DISTRIBUTIONS.name,
                totals.totalDistributions,
            );
            const toNode = createNodeName(
                NODE_TYPES.TAXES.name,
                totals.totalTaxes,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.DISTRIBUTIONS.color);
            nodeColorMap.set(toNode, NODE_TYPES.TAXES.color);

            rows.push([fromNode, toNode, totals.totalDistributionTaxes]);
        }

        // Social Security Benefits → Taxes (if taxed)
        if (
            totals.totalSocialSecurity > 0 &&
            totals.totalSocialSecurityTaxes > 0
        ) {
            const fromNode = createNodeName(
                NODE_TYPES.SOCIAL_SECURITY.name,
                totals.totalSocialSecurity,
            );
            const toNode = createNodeName(
                NODE_TYPES.TAXES.name,
                totals.totalTaxes,
            );

            nodeColorMap.set(fromNode, NODE_TYPES.SOCIAL_SECURITY.color);
            nodeColorMap.set(toNode, NODE_TYPES.TAXES.color);

            rows.push([fromNode, toNode, totals.totalSocialSecurityTaxes]);
        }

        // Taxes → Individual account tax breakdown
        cashFlow.distributions.forEach((distribution) => {
            if (distribution.taxAmount > 0) {
                const fromNode = createNodeName(
                    NODE_TYPES.TAXES.name,
                    totals.totalTaxes,
                );
                const toNode = createNodeName(
                    `${distribution.account.id} (Tax)`,
                    distribution.taxAmount,
                );

                nodeColorMap.set(fromNode, NODE_TYPES.TAXES.color);
                nodeColorMap.set(toNode, NODE_TYPES.TAXES.color);

                rows.push([fromNode, toNode, distribution.taxAmount]);
            }
        });

        // Taxes → Total Social Security Tax
        if (totals.totalSocialSecurityTaxes > 0) {
            const fromNode = createNodeName(
                NODE_TYPES.TAXES.name,
                totals.totalTaxes,
            );

            // SS Tax
            if (cashFlow.totalSocialSecurityTax > 0) {
                const toNode = createNodeName(
                    'Total Social Security Tax',
                    cashFlow.totalSocialSecurityTax,
                );
                nodeColorMap.set(fromNode, NODE_TYPES.TAXES.color);
                nodeColorMap.set(toNode, NODE_TYPES.TAXES.color);
                rows.push([fromNode, toNode, cashFlow.totalSocialSecurityTax]);
            }
        }

        const data: Array<Array<string | number>> = [
            ['From', 'To', 'Weight'],
            ...rows,
        ];

        const chartOptions = {
            sankey: {
                node: {
                    label: {
                        color: tokens.color.light.brand.white,
                        fontSize: 12,
                        bold: true,
                    },
                    width: 20,
                    colors: Array.from(nodeColorMap.values()),
                },
                link: {
                    colorMode: 'gradient',
                },
            },
        };

        return { data, hasData: rows.length > 0, options: chartOptions };
    }, [annualCashFlows, selectedYear]);

    const { data: chartData, hasData, options } = chartDataResult;

    return (
        <YStack
            gap={16}
            width="100%"
            height={500}
            backgroundColor="$gray400"
            marginVertical={-16}
            padding={16}
            borderRadius={4}
            overflow="hidden"
        >
            {hasData ? (
                <Chart
                    chartType="Sankey"
                    width="100%"
                    height="100%"
                    data={chartData}
                    options={options}
                    loader={
                        <View
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                        >
                            <BodyMediumRegular color="$gray600">
                                Loading chart…
                            </BodyMediumRegular>
                        </View>
                    }
                />
            ) : (
                <View alignItems="center" justifyContent="center" height="100%">
                    <BodyMediumRegular color="$gray600">
                        No data available for this year
                    </BodyMediumRegular>
                </View>
            )}
        </YStack>
    );
};
